import FacebookIcon from '@mui/icons-material/Facebook';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Typography
} from '@mui/material';
import { useConfig } from '@revenuewell/logic-config';
import { useOidcWithAdmin } from '@revenuewell/logic-oidc';
import { SocialMediaService } from 'apps/wrapper/src/services/social-media/social-media-service';
import { FacebookPageInfo } from 'apps/wrapper/src/services/social-media/types/facebook-page-info';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from '../../../dialog/dialog.styles';

type Step = 'confirm' | 'waitingForOAuth' | 'selectPage' | 'saving' | 'error' | 'blocked';

export interface ConnectFacebookDialogProps {
  open: boolean;
  onClose: () => void;
  locationId: number;
  facebookAppId?: string | null;
  facebookPermissionScopes?: string | null;
  /** Called after the page is successfully saved. Receives the connected page name. */
  onConnected: (pageName: string) => void;
}

export const ConnectFacebookDialog: FC<ConnectFacebookDialogProps> = ({
  open,
  onClose,
  locationId,
  facebookAppId,
  facebookPermissionScopes,
  onConnected
}) => {
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const [step, setStep] = useState<Step>('confirm');
  const [pages, setPages] = useState<FacebookPageInfo[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [userAccessToken, setUserAccessToken] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const socialMediaService = useMemo(() => new SocialMediaService(config, oidcService), [config, oidcService]);

  useEffect(() => {
    if (!open) {
      cleanup();
      setStep('confirm');
      setPages([]);
      setSelectedPageId(null);
      setUserAccessToken(null);
      setErrorMessage(null);
    }
  }, [open]);

  const cleanup = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (messageHandlerRef.current) {
      window.removeEventListener('message', messageHandlerRef.current);
      messageHandlerRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
  };

  const buildOAuthUrl = () => {
    const redirectUri = `${config.socialMedia?.apiUrl}/facebook-oauth-callback`;
    return (
      `https://www.facebook.com/dialog/oauth` +
      `?client_id=${facebookAppId ?? ''}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${encodeURIComponent(facebookPermissionScopes ?? '')}` +
      `&response_type=token` +
      `&display=popup` +
      `&state=${encodeURIComponent(window.location.origin)}`
    );
  };

  const handleConnect = () => {
    setStep('waitingForOAuth');

    const oauthUrl = buildOAuthUrl();
    const popup = window.open(oauthUrl, 'facebook-oauth', 'width=600,height=700,scrollbars=yes');

    if (!popup) {
      setStep('blocked');
      return;
    }

    popupRef.current = popup;
    popup.focus();

    const handleMessage = async (event: MessageEvent) => {
      const apiOrigin = new URL(config.socialMedia.apiUrl).origin;
      if (event.origin !== apiOrigin) return;

      if (event.data?.type === 'facebook-oauth-success') {
        cleanup();
        const accessToken = event.data.accessToken as string;
        try {
          const fetchedPages = await socialMediaService.getFacebookPages(locationId, accessToken);
          setUserAccessToken(accessToken);
          setPages(fetchedPages);
          setStep('selectPage');
        } catch {
          setErrorMessage('Failed to load Facebook pages. Please try again.');
          setStep('error');
        }
      } else if (event.data?.type === 'facebook-oauth-error') {
        cleanup();
        setErrorMessage(event.data.errorDescription || 'Facebook authorization failed.');
        setStep('error');
      }
    };

    messageHandlerRef.current = handleMessage;
    window.addEventListener('message', handleMessage);

    // Poll for popup close (user dismissed without completing OAuth)
    const pollTimer = setInterval(() => {
      if (popup?.closed) {
        clearInterval(pollTimer);
        if (messageHandlerRef.current) {
          cleanup();
          setStep('confirm');
        }
      }
    }, 500);
    pollTimerRef.current = pollTimer;
  };

  const handleReopenPopup = () => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus();
    } else {
      // Popup was closed by the user — restart the flow
      cleanup();
      handleConnect();
    }
  };

  const handleSave = async () => {
    if (!selectedPageId || !userAccessToken) return;
    const pageName = pages.find(p => p.id === selectedPageId)?.name ?? null;
    try {
      setStep('saving');
      await socialMediaService.connectFacebook(locationId, { userAccessToken, pageId: selectedPageId });
      onConnected(pageName ?? '');
      onClose();
    } catch {
      setErrorMessage('Failed to connect Facebook. Please try again.');
      setStep('error');
    }
  };

  return (
    <Dialog open={open} maxWidth={false}>
      {step === 'confirm' && (
        <>
          <DialogTitle sx={{ cursor: 'default' }}>Connect to Facebook</DialogTitle>
          <DialogContent sx={{ width: '470px', padding: '10px 24px 24px 24px', lineHeight: '20px' }}>
            Your Facebook profile is not connected to RevenueWell.
            <br />
            Do you want to connect it now?
          </DialogContent>
          <DialogActions sx={{ padding: '10px 24px 24px 24px' }}>
            <Button variant="outlined" size="medium" onClick={onClose} disableElevation>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="medium"
              onClick={handleConnect}
              startIcon={<FacebookIcon />}
              disableElevation
              sx={{ backgroundColor: '#0675DD', '&:hover': { backgroundColor: '#267fec' }, textTransform: 'none' }}
            >
              Connect
            </Button>
          </DialogActions>
        </>
      )}

      {step === 'waitingForOAuth' && (
        <>
          <DialogTitle sx={{ cursor: 'default' }}>Connect to Facebook</DialogTitle>
          <DialogContent sx={{ width: '470px', padding: '10px 24px 24px 24px' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <CircularProgress size={20} sx={{ mt: '2px', flexShrink: 0 }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  A Facebook window was opened.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Complete the authorization there to continue. If you don't see it, click the button below to bring it
                  back.
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ padding: '10px 24px 24px 24px' }}>
            <Button variant="outlined" size="medium" onClick={onClose} disableElevation>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="medium"
              onClick={handleReopenPopup}
              startIcon={<FacebookIcon />}
              disableElevation
              sx={{ backgroundColor: '#0675DD', '&:hover': { backgroundColor: '#267fec' }, textTransform: 'none' }}
            >
              Reopen Facebook window
            </Button>
          </DialogActions>
        </>
      )}

      {step === 'blocked' && (
        <>
          <DialogTitle sx={{ cursor: 'default' }}>Popup Blocked</DialogTitle>
          <DialogContent sx={{ width: '470px', padding: '10px 24px 24px 24px' }}>
            <Typography variant="body2">
              Your browser blocked the Facebook authorization window. Please allow popups for this site and try again.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ padding: '10px 24px 24px 24px' }}>
            <Button variant="outlined" size="medium" onClick={onClose} disableElevation>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="medium"
              onClick={() => { setStep('confirm'); }}
              disableElevation
              sx={{ backgroundColor: '#0675DD', '&:hover': { backgroundColor: '#267fec' }, textTransform: 'none' }}
            >
              Try again
            </Button>
          </DialogActions>
        </>
      )}

      {step === 'selectPage' && (
        <>
          <DialogTitle sx={{ cursor: 'default' }}>Select Facebook Page</DialogTitle>
          <DialogContent sx={{ width: '470px', padding: '10px 24px 24px 24px' }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {pages.length} page{pages.length !== 1 ? 's' : ''} found. Select the Facebook page you want to connect:
            </Typography>
            {pages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No pages found. Make sure you have admin access to at least one Facebook page.
              </Typography>
            ) : (
              <List dense sx={{ border: '1px solid #E9EAEE', borderRadius: 1, maxHeight: 240, overflow: 'auto' }}>
                {pages.map((page) => (
                  <ListItemButton
                    key={page.id}
                    selected={selectedPageId === page.id}
                    onClick={() => setSelectedPageId(page.id)}
                  >
                    <ListItemText primary={page.name} secondary={page.category} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions sx={{ padding: '10px 24px 24px 24px' }}>
            <Button variant="outlined" size="medium" onClick={onClose} disableElevation>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="medium"
              onClick={handleSave}
              disabled={!selectedPageId}
              disableElevation
              sx={{ backgroundColor: '#0675DD', '&:hover': { backgroundColor: '#267fec' }, textTransform: 'none' }}
            >
              Connect page
            </Button>
          </DialogActions>
        </>
      )}

      {step === 'saving' && (
        <>
          <DialogTitle sx={{ cursor: 'default' }}>Connecting...</DialogTitle>
          <DialogContent sx={{ width: '470px', padding: '10px 24px 24px 24px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Saving your Facebook connection...</Typography>
            </Box>
          </DialogContent>
        </>
      )}

      {step === 'error' && (
        <>
          <DialogTitle sx={{ cursor: 'default' }}>Connect to Facebook</DialogTitle>
          <DialogContent sx={{ width: '470px', padding: '10px 24px 24px 24px', color: '#d32f2f' }}>
            {errorMessage || 'An error occurred. Please try again.'}
          </DialogContent>
          <DialogActions sx={{ padding: '10px 24px 24px 24px' }}>
            <Button variant="outlined" size="medium" onClick={onClose} disableElevation>
              Close
            </Button>
            <Button
              variant="contained"
              size="medium"
              onClick={() => setStep('confirm')}
              disableElevation
              sx={{ backgroundColor: '#0675DD', '&:hover': { backgroundColor: '#267fec' }, textTransform: 'none' }}
            >
              Try again
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
